<script>
  /**
   * Partitions the image into two honest zones while editing:
   * clear = survives the crop of EVERY brand format (at the current focal point),
   * striped = cropped away by at least one format.
   * Moves live while the focal point is dragged; disappears entirely when the
   * image is set to "show whole" (nothing crops, so nothing to warn about).
   */
  import { getContext } from 'svelte';
  import { convert_string_to_float } from '#lib/util.js';

  let {
    width: image_width,
    height: image_height,
    focal_point_x = 0.5,
    focal_point_y = 0.5,
    fit_to_frame = false,
  } = $props();

  let site_settings = getContext('site_settings');

  /**
   * Intersection of all cover-crop windows, in percent of the image.
   * For frame ratio r vs native n (both w/h), object-position semantics give
   * the visible window: wider frame → full width, height fraction n/r anchored
   * at focal_y; narrower frame → full height, width fraction r/n at focal_x.
   */
  let safe_region = $derived.by(() => {
    const n = image_width / image_height;
    if (!Number.isFinite(n) || n <= 0) return null;
    let x0 = 0, x1 = 1, y0 = 0, y1 = 1;
    let crops_anything = false;
    for (const ratio_str of site_settings?.aspect_ratios || []) {
      const r = convert_string_to_float(ratio_str);
      if (!Number.isFinite(r) || r <= 0) continue;
      if (Math.abs(r - n) < 0.005) continue;
      crops_anything = true;
      if (r > n) {
        const h = n / r;
        const start = focal_point_y * (1 - h);
        y0 = Math.max(y0, start);
        y1 = Math.min(y1, start + h);
      } else {
        const w = r / n;
        const start = focal_point_x * (1 - w);
        x0 = Math.max(x0, start);
        x1 = Math.min(x1, start + w);
      }
    }
    if (!crops_anything) return null;
    return { x0: x0 * 100, x1: x1 * 100, y0: y0 * 100, y1: y1 * 100 };
  });
</script>

{#if !fit_to_frame && safe_region}
  <div
    class="safe-region-wrapper"
    style:--image-aspect-ratio={image_width && image_height ? `${image_width} / ${image_height}` : '1 / 1'}
  >
    <!-- Four striped shades around the always-visible window -->
    <div class="shade" style={`inset: 0 0 ${100 - safe_region.y0}% 0;`}></div>
    <div class="shade" style={`inset: ${safe_region.y1}% 0 0 0;`}></div>
    <div class="shade" style={`inset: ${safe_region.y0}% ${100 - safe_region.x0}% ${100 - safe_region.y1}% 0;`}></div>
    <div class="shade" style={`inset: ${safe_region.y0}% 0 ${100 - safe_region.y1}% ${safe_region.x1}%;`}></div>
    <div
      class="safe-window"
      style={`inset: ${safe_region.y0}% ${100 - safe_region.x1}% ${100 - safe_region.y1}% ${safe_region.x0}%;`}
    ></div>
  </div>
{/if}

<style>
  .safe-region-wrapper {
    position: absolute;
    inset: 0;
    margin: auto;
    width: auto;
    height: 100%;
    max-width: 100%;
    max-height: 100%;
    aspect-ratio: var(--image-aspect-ratio);
    overflow: hidden;
    pointer-events: none;
  }

  .shade {
    position: absolute;
    /* The system's editing overlay pattern (see .block-selected in editing.css) */
    background-color: color-mix(in oklab, var(--editing) 10%, transparent);
    background-image: linear-gradient(-45deg, var(--editing-800) 10%, transparent 10%, transparent 40%, var(--editing-800) 40%, var(--editing-800) 60%, transparent 60%, transparent 90%, var(--editing-800) 90%);
    background-size: 4px 4px;
    transition-property: inset;
    transition-duration: 120ms;
    transition-timing-function: var(--accelerated-ease-out, ease-out);
  }

  .safe-window {
    position: absolute;
    border: 1px solid var(--editing);
    transition-property: inset;
    transition-duration: 120ms;
    transition-timing-function: var(--accelerated-ease-out, ease-out);
  }

  @media (prefers-reduced-motion: reduce) {
    .shade,
    .safe-window {
      transition-duration: 0s;
    }
  }
</style>
